import type { ValidationField } from "./types/consoleTypes";

export type ValidationDisplayRow = {
  key: string;
  depth: number;
  parameter: string;
  nodeName: string; // The "Human-Friendly" parameter label
  nodeType: "subsystem" | "channel" | "sensor"; // Satellite domain iconography
  fullPath: string; // Absolute Telemetry/Command path
  hasChildren: boolean;
  isLastChild: boolean;
  ancestorRails: boolean[]; // True if a vertical line should be drawn at this level's index
  parentKey?: string;
  validations?: string;
  resultValue?: string;
  resultStatus?: string;
  hasChildFailure?: boolean;
  statusSummary?: string; // e.g., "✖ 2 Fails" (Anomalies)
  valueType?: "number" | "boolean" | "string" | "null" | "array" | "object";
};

/** Backend often sends array items as Parameter "0", "1"; show as parent[0]. */
function isArrayIndexParameter(name: string): boolean {
  return /^\d+$/.test(String(name).trim());
}

function getStatusCounts(node: ValidationField): { pass: number; fail: number; timeout: number } {
  let pass = 0;
  let fail = 0;
  let timeout = 0;

  const s = String(node.ResultStatus ?? "").toLowerCase().trim();
  if (s === "pass") pass++;
  else if (s === "fail") fail++;
  else if (s === "timeout" || s === "timed_out" || s === "timedout") timeout++;

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      const counts = getStatusCounts(child);
      pass += counts.pass;
      fail += counts.fail;
      timeout += counts.timeout;
    });
  }

  return { pass, fail, timeout };
}

export function flattenValidationRows(
  nodes: ValidationField[],
  depth = 0,
  parentKey = "root",
  ancestorRails: boolean[] = [],
  parentPath = "",
): ValidationDisplayRow[] {
  return nodes.reduce<ValidationDisplayRow[]>((acc, node, index) => {
    const rawParam = node.Parameter ?? "—";

    const key = `${parentKey}-${index}-${rawParam}`;
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    const isLastChild = index === nodes.length - 1;

    const isArrayIndex = isArrayIndexParameter(rawParam);
    let nodeName = rawParam;
    if (isArrayIndex) {
      nodeName = `#${Number(rawParam) + 1}`; // [0] -> #1
    }

    let nodeType: ValidationDisplayRow["nodeType"] = "sensor";
    if (hasChildren) {
      const isArray = node.type?.toLowerCase().trim() === "array";
      nodeType = isArray ? "channel" : "subsystem";
      if (isArray) {
        nodeName = `${nodeName} (${children.length} ${
          children.length === 1 ? "item" : "items"
        })`;
      }
    }

    // Build the full path for tooltips
    const fullPath =
      parentPath === ""
        ? rawParam
        : isArrayIndex
        ? `${parentPath}[${rawParam}]`
        : `${parentPath}.${rawParam}`;

    let statusSummary: string | undefined;
    let hasChildFailure = false;
    if (hasChildren) {
      const { pass, fail, timeout } = getStatusCounts(node);
      if (fail > 0 && timeout > 0) {
        statusSummary = `✖ ${fail} ${fail === 1 ? "Fail" : "Fails"}, ⏱ ${timeout} Timeout`;
        hasChildFailure = true;
      } else if (fail > 0) {
        statusSummary = `✖ ${fail} ${fail === 1 ? "Fail" : "Fails"}`;
        hasChildFailure = true;
      } else if (timeout > 0) {
        statusSummary = `⏱ ${timeout} Timeout`;
        hasChildFailure = true;
      }
    } else {
      const s = String(node.ResultStatus ?? "").toLowerCase().trim();
      hasChildFailure = s === "fail" || s === "timeout" || s === "timed_out" || s === "timedout";
    }

    let valueType: ValidationDisplayRow["valueType"] = "string";
    if (hasChildren) {
      valueType = node.type?.toLowerCase().trim() === "array" ? "array" : "object";
    } else {
      const v = String(node.ResultValue ?? "").trim();
      if (v === "" || v === "—") valueType = "null";
      else if (v.toLowerCase() === "true" || v.toLowerCase() === "false") valueType = "boolean";
      else if (!isNaN(Number(v)) && !v.includes(" ") && v !== "") valueType = "number";
    }

    acc.push({
      key,
      depth,
      hasChildren,
      isLastChild,
      ancestorRails: [...ancestorRails],
      parentKey: parentKey !== "root" ? parentKey : undefined,
      parameter: rawParam,
      nodeName,
      nodeType,
      fullPath,
      validations: node.Validations ?? undefined,
      resultValue: node.ResultValue ?? undefined,
      resultStatus: node.ResultStatus ?? undefined,
      hasChildFailure,
      statusSummary,
      valueType,
    });

    if (hasChildren) {
      // For children, add a rail at the current depth ONLY if this node has siblings below it
      const nextAncestorRails = [...ancestorRails];
      nextAncestorRails[depth] = !isLastChild;

      acc.push(
        ...flattenValidationRows(
          children,
          depth + 1,
          key,
          nextAncestorRails,
          fullPath,
        ),
      );
    }

    return acc;
  }, []);
}
